/**
 * Typed HTTP client for the NestJS API.
 *
 * Default base is the RELATIVE `/api` prefix: in every deployment the ingress
 * routes `/api/*` to the api service and strips the prefix, so the browser
 * talks to the same origin and no API host is baked into the bundle. `VITE_API_URL`
 * is only set for unusual contours — Vite's dev server proxies `/api` to :3001
 * (see vite.config.ts).
 */

import type {
  AddAdminDto,
  AlertRuleDto,
  AdminDto,
  AppOverviewDto,
  AuthMe,
  CheckHistoryPoint,
  CheckSummaryDto,
  HealthResponse,
  NotificationChannelDto,
} from '@ops-monitor/shared';

export type {
  AddAdminDto,
  AlertRuleDto,
  AdminDto,
  AppOverviewDto,
  AuthMe,
  CheckHistoryPoint,
  CheckSummaryDto,
  HealthResponse,
  NotificationChannelDto,
};

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

/** An API error with its HTTP status and the NestJS message/body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly method: string,
    readonly path: string,
    message: string,
  ) {
    super(`${method} ${path} failed with HTTP ${status}: ${message}`);
    this.name = 'ApiError';
  }
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string') return body.message;
    return JSON.stringify(body);
  } catch {
    return response.statusText || 'unknown error';
  }
}

/**
 * A 401 means the session is gone: land on the in-SPA login route with a FULL
 * page load (client-side routing would leave already-mounted queries holding a
 * stale 401). `/auth/*` is excluded — those calls decide the logged-out state.
 */
function handleUnauthorized(path: string): void {
  if (path.startsWith('/auth')) return;
  window.location.assign('/login');
}

async function request<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(path);
    throw new ApiError(response.status, method, path, await parseError(response));
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function jsonBody(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

export const health = {
  /** GET /healthz */
  get: (signal?: AbortSignal) => request<HealthResponse>('GET', '/healthz', { signal }),
};

/**
 * Session identity. A 401 here is meaningful — the caller decides what to do
 * (the layout shows the login state, it does not redirect).
 */
export const auth = {
  me: (signal?: AbortSignal) => request<AuthMe>('GET', '/auth/me', { signal }),
  requestCode: (email: string) => request<{ ok: boolean; sent?: boolean; error?: string }>('POST', '/auth/code', jsonBody('POST', { email })),
  verify: (email: string, code: string) => request<{ ok: true; user: { email: string } }>('POST', '/auth/verify', jsonBody('POST', { email, code })),
  /** POST (never a side-effect GET), then a full re-mount on /login. */
  logout: async (): Promise<void> => {
    await request('POST', '/auth/logout', jsonBody('POST', {}));
    window.location.assign('/login');
  },
};

export const monitoring = {
  apps: (signal?: AbortSignal) => request<{ items: AppOverviewDto[] }>('GET', '/monitoring/apps', { signal }),
  checks: (app?: string, signal?: AbortSignal) =>
    request<{ items: CheckSummaryDto[] }>('GET', `/monitoring/checks${app ? `?app=${encodeURIComponent(app)}` : ''}`, { signal }),
  sections: (signal?: AbortSignal) =>
    request<{ items: { key: string; title: string; description: string; apps: string[] }[] }>('GET', '/monitoring/sections', { signal }),
  history: (id: string, hours: number, signal?: AbortSignal) =>
    request<{ items: CheckHistoryPoint[] }>('GET', `/monitoring/checks/${encodeURIComponent(id)}/history?hours=${hours}`, { signal }),
  runNow: (id: string) => request<CheckSummaryDto>('POST', `/monitoring/checks/${encodeURIComponent(id)}/run`, { method: 'POST' }),
};

export const alerts = {
  list: (signal?: AbortSignal) => request<{ items: AlertRuleDto[] }>('GET', '/alerts', { signal }),
  get: (checkId: string, signal?: AbortSignal) =>
    request<AlertRuleDto | { exists: false }>('GET', `/alerts/${encodeURIComponent(checkId)}`, { signal }),
  upsert: (
    checkId: string,
    input: {
      enabled: boolean;
      level: 'warn' | 'crit';
      threshold: number | null;
      direction: 'above' | 'below';
      fieldConditions: { field: string; equals: string; metric: string; threshold: number; direction: 'above' | 'below' }[];
      consecutiveFires: number;
      timeFrom: string;
      timeTo: string;
      eventRetentionDays: number;
      /** Comma-separated Slack member IDs ('' = nobody). */
      mentionSlackIds: string;
      /** Delivery targets; [] = the default channel. */
      channelIds: string[];
    },
  ) => request<AlertRuleDto>('PUT', `/alerts/${encodeURIComponent(checkId)}`, jsonBody('PUT', input)),
};

/** Notification channels («Каналы уведомлений» on «Настройки»). */
export const channels = {
  list: (signal?: AbortSignal) => request<{ items: NotificationChannelDto[] }>('GET', '/channels', { signal }),
  create: (input: { name: string; webhookUrl: string; isDefault: boolean; type?: string }) =>
    request<NotificationChannelDto>('POST', '/channels', jsonBody('POST', input)),
  update: (id: string, input: { name?: string; webhookUrl?: string; isDefault?: boolean; type?: string }) =>
    request<NotificationChannelDto>('PATCH', `/channels/${encodeURIComponent(id)}`, jsonBody('PATCH', input)),
  remove: (id: string) => request<{ ok: true }>('DELETE', `/channels/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const admins = {
  list: (signal?: AbortSignal) => request<{ items: AdminDto[] }>('GET', '/admins', { signal }),
  add: (email: string) => request<{ email: string }>('POST', '/admins', jsonBody('POST', { email })),
  remove: (email: string) => request<{ ok: true }>('DELETE', `/admins/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  audit: (signal?: AbortSignal) =>
    request<{ items: { id: string; kind: string; email: string | null; details: string | null; createdAt: string }[] }>('GET', '/admins/audit', { signal }),
};
